import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_DESTINATION_ZONE_LIST_CONFIGURATION, EMPTY_DESTINATION_ZONE_LIST_CRITERIA } from "../constant/destinationZoneConstant";
import { DestinationZone } from "../objects/destinationZone";
import { DestinationZoneState } from "../objects/destinationZoneState";
import { setDestinationZonePreference } from "../utils/setDestinationZonePreference";
import { Column } from "../objects/column";
import { DestinationZoneListCriteria } from "../objects/destinationZoneTypeList";


const initialState: DestinationZoneState = {
    destination_zone_list: [],
    destination_zone_list_configuration: EMPTY_DESTINATION_ZONE_LIST_CONFIGURATION,
    destination_zone_list_loading: false,
    destination_zone_list_count: 0,
    destination_zone_list_criteria: EMPTY_DESTINATION_ZONE_LIST_CRITERIA,
    destination_zone: null,
    first_preference_loaded: false,
    delete_destination_zone: null,
    duplicate_destination_zone: null,
    destination_zone_list_loaded: false,
    destination_zone_list_refresh: false,
    destination_zone_is_creating: false,
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');
const updateDestinationZoneIsCreatingReducer = (state: DestinationZoneState, action: PayloadAction<boolean>): DestinationZoneState => ({
    ...state, destination_zone_is_creating: action.payload
});
const setDeleteDestinationZoneReducer = (state: DestinationZoneState, action: PayloadAction<DestinationZone | null>): DestinationZoneState => ({
    ...state, delete_destination_zone: action.payload
});
const setDuplicateDestinationZoneReducer = (state: DestinationZoneState, action: PayloadAction<DestinationZone | null>): DestinationZoneState => ({
    ...state, duplicate_destination_zone: action.payload
});

const addDestinationZoneListConfigurationPreferenceReducer = (state: DestinationZoneState, action: PayloadAction<Preferences[]>): DestinationZoneState => ({
    ...state, destination_zone_list_configuration: { ...state.destination_zone_list_configuration, preferences: [...state.destination_zone_list_configuration.preferences, ...action.payload] }
});

const setDestinationZoneListConfigurationPreferenceReducer = (state: DestinationZoneState, action: PayloadAction<Preferences[]>): DestinationZoneState => ({
    ...state, destination_zone_list_configuration: { ...state.destination_zone_list_configuration, preferences: action.payload }
});

const setPreferenceReducer = (state: DestinationZoneState, action: PayloadAction<Preferences[]>): DestinationZoneState => {
    const { preferences, columns, order_by, order } = setDestinationZonePreference(action.payload, state.destination_zone_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        destination_zone_list_configuration: { ...state.destination_zone_list_configuration, preferences, columns },
        destination_zone_list_criteria: { ...state.destination_zone_list_criteria, order, order_by }
    };
};

const toggleColumnReducer = (state: DestinationZoneState, action: PayloadAction<number>): DestinationZoneState => {
    const columns = cloneDeep([...state.destination_zone_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, destination_zone_list_configuration: { ...state.destination_zone_list_configuration, columns } };
};
const toggleDestinationZoneRefreshListReducer = (state: DestinationZoneState): DestinationZoneState => ({
    ...state, destination_zone_list_refresh: !state.destination_zone_list_refresh, destination_zone_list_loaded: false
});
const updateDestinationZoneListReducer = (state: DestinationZoneState, action: PayloadAction<DestinationZone[]>): DestinationZoneState => ({
    ...state, destination_zone_list: action.payload, destination_zone_list_loaded: true
});
const updateDestinationZoneListCriteriaReducer = (state: DestinationZoneState, action: PayloadAction<DestinationZoneListCriteria>): DestinationZoneState => ({
    ...state, destination_zone_list_criteria: action.payload
});
const updateDestinationZoneReducer = (state: DestinationZoneState, action: PayloadAction<DestinationZone | null>): DestinationZoneState => ({
    ...state, destination_zone: action.payload
});
const updateDestinationZoneListLoadingReducer = (state: DestinationZoneState, action: PayloadAction<boolean>): DestinationZoneState => ({
    ...state, destination_zone_list_loading: action.payload
});
const updateDestinationZoneListCountReducer = (state: DestinationZoneState, action: PayloadAction<number>): DestinationZoneState => ({
    ...state, destination_zone_list_count: action.payload
});

const setColumnsReducer = (state: DestinationZoneState, action: PayloadAction<Column[]>): DestinationZoneState => ({
    ...state, destination_zone_list_configuration: { ...state.destination_zone_list_configuration, columns: action.payload },
});

export const destinationZone = createSlice({
    name: 'destination_zone',
    initialState,
    reducers: {
        updateDestinationZoneIsCreating: updateDestinationZoneIsCreatingReducer,
        toggleDestinationZoneRefreshList: toggleDestinationZoneRefreshListReducer,
        setDeleteDestinationZone: setDeleteDestinationZoneReducer,
        setDuplicateDestinationZone: setDuplicateDestinationZoneReducer,
        addDestinationZoneListConfigurationPreference: addDestinationZoneListConfigurationPreferenceReducer,
        setDestinationZoneListConfigurationPreference: setDestinationZoneListConfigurationPreferenceReducer,
        toggleColumn: toggleColumnReducer,
        updateDestinationZoneList: updateDestinationZoneListReducer,
        updateDestinationZoneListCriteria: updateDestinationZoneListCriteriaReducer,
        updateDestinationZone: updateDestinationZoneReducer,
        updateDestinationZoneListLoading: updateDestinationZoneListLoadingReducer,
        updateDestinationZoneListCount: updateDestinationZoneListCountReducer,
        setColumns: setColumnsReducer
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    updateDestinationZoneIsCreating,
    setDeleteDestinationZone,
    setDuplicateDestinationZone,
    addDestinationZoneListConfigurationPreference,
    setDestinationZoneListConfigurationPreference,
    setColumns,
    toggleColumn,
    updateDestinationZoneList,
    updateDestinationZoneListCriteria,
    updateDestinationZone,
    updateDestinationZoneListLoading,
    updateDestinationZoneListCount,
    toggleDestinationZoneRefreshList
} = destinationZone.actions;
